import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, FormControl, FormHelperText, Stack, Typography } from '@mui/material';
import { Card, CardContent, CardHeader, FormLabel } from './CampaignWizardStyles';
import { useQuery } from '@tanstack/react-query';

import { CampaignWizardStepProps } from './CampaignWizard';
import { Dealer } from '../../../../types/Dealer';
import { DspGeoLocation } from '../../../../types/DspGeoLocation';
import { Location } from '../../../../types/Location';
import { ZipCode } from '../../../../types/Location';
import { toCampaignAdGroupLocations } from '../../../../hooks/useLocations';
import { useCampaignWizardContext } from '../../../../hooks/useCampaignWizard';
import { useLocationBoundaries } from '../../../../hooks/useMapBoundaries';
import { useMapToken } from '../../../../hooks/Models/MapToken';
import ApiService from '../../../../ApiService';
import LocationSourceField from '../../../../components/Location/LocationSourceField';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';

type CampaignTargetingLocationDrawerCardProps = {} & CampaignWizardStepProps;

export default function CampaignTargetingLocationDrawerCard(props: CampaignTargetingLocationDrawerCardProps) {
    const { campaign, onChange } = props;
    const { dealer } = campaign;

    const country = 'United States';
    const countryCode = 'US';
    const baseUrl = '/api/geographicTargeting';

    const { mapToken } = useMapToken();

    const $campaignWizard = useCampaignWizardContext();
    const { hasError } = $campaignWizard;
    const { isCompletedCampaign, isDraftCampaign, isNewCampaign } = $campaignWizard;

    const { $locations, $loader } = $campaignWizard;
    const {
        locations: selections = [],
        fetchLocations: fetchSelections = () => {},
        isFetchingLocations: isFetchingSelections = false,
    } = $locations || {};

    const adGroupLocations = useMemo(() => {
        return campaign.adGroups?.[0]?.adGroupLocations ?? [];
    }, [campaign.adGroups]);

    const { data: initialDspLocations } = useQuery({
        queryKey: ['campaign/wizard/dspLocations/initial', adGroupLocations],
        queryFn: async () => {
            const { data } = await ApiService.getDspGeoLocations({
                ids: adGroupLocations?.map(({ dspGeoLocationId }) => dspGeoLocationId),
            });

            return data;
        },
        enabled: !!adGroupLocations.length,
        refetchOnWindowFocus: false,
    });

    const { data: defaultDspLocations } = useQuery({
        queryKey: ['campaign/wizard/dspLocations/default', dealer?.zip],
        queryFn: async () => {
            const { data } = await ApiService.getDspGeoLocations({
                zipcodes: [dealer?.zip as string],
            });

            return data;
        },
        enabled: !!dealer && !!dealer.zip,
        refetchOnWindowFocus: false,
    });

    const getCenterPoint = useCallback(
        async (country: string, zipCode: string) => {
            if (!mapToken) {
                return { center: [-97.9222112121185, 39.3812661305678] };
            }

            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${zipCode}.json?limit=1&country=${countryCode}&types=postcode&access_token=${mapToken}`
            );
            const data = await response.json();

            if (data.features.length > 0) {
                return { center: data.features[0].center };
            }

            // USA
            return { center: [-97.9222112121185, 39.3812661305678] };
        },
        [mapToken]
    );

    const $locationBoundaries = useLocationBoundaries({
        baseUrl: baseUrl,
        country: country,
        getCenterPoint: getCenterPoint,
    });

    const [locations, setLocations] = useState<Location[]>([]);

    const [centerPoint, setCenterPoint] = useState('');
    const [mapCenter, setMapCenter] = useState('');

    function getAddress(dealer: Dealer, zipcodes?: string): string {
        let address = '';
        if (dealer) {
            if (dealer.address) address = dealer.address;
            if (dealer.city) address += ' ' + dealer.city;
            if (dealer.state) address += ' ' + dealer.state;
            if (dealer.zip) address += ' ' + dealer.zip;
        }
        if (address === '' && zipcodes && zipcodes.length > 0) {
            address = zipcodes.split(',')[0];
        }
        return address;
    }

    const handleRecenterLocation = useCallback((): void => {
        const lastLocation: Location | undefined = locations?.[locations.length - 1];
        const rawCenterPoint: string = lastLocation?.zipCode ?? lastLocation?.name ?? '';

        setMapCenter(rawCenterPoint ? rawCenterPoint : centerPoint);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations, onChange]);

    const assignDefaultAdvertiserLocation = useCallback(() => {
        if (dealer && defaultDspLocations !== undefined) {
            if (defaultDspLocations?.length) {
                setLocations(
                    defaultDspLocations?.map((dspLocation: DspGeoLocation) => ({
                        id: dspLocation.geoId,
                        name: dspLocation.name,
                        category: dspLocation.category,
                        zipCode: dspLocation.zipcode,
                        radius: 30,
                    })) || []
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealer, defaultDspLocations]);

    useEffect(() => {
        let rawCenterPoint = '';

        if (locations.length > 0) {
            const lastLocation: Location | undefined = locations?.[locations.length - 1];
            rawCenterPoint = lastLocation?.name ?? '';
        }

        if (!centerPoint.length) {
            if (dealer) {
                rawCenterPoint = getAddress(dealer, campaign.zipcodes);
            }

            setCenterPoint(rawCenterPoint);
            setMapCenter(rawCenterPoint);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealer, campaign.zipcodes, locations]);

    useEffect(() => {
        if ($loader) {
            if (isFetchingSelections) {
                $loader.loading('fetchLocationSources');
            } else {
                $loader.loaded('fetchLocationSources');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchingSelections]);

    useEffect(() => {
        if (campaign?.adGroups?.length) {
            if (initialDspLocations?.length) {
                setLocations(
                    initialDspLocations?.map((dspLocation: DspGeoLocation) => ({
                        id: dspLocation.geoId,
                        name: dspLocation.name,
                        category: dspLocation.category,
                        zipCode: dspLocation.zipcode,
                        radius: 30,
                    })) || []
                );
            } else {
                assignDefaultAdvertiserLocation();
            }
        } else {
            setLocations([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign.adGroups, assignDefaultAdvertiserLocation, initialDspLocations]);

    const updateLocations = useCallback(
        async (newLocations: Location[]) => {
            setLocations(newLocations);

            if (onChange) {
                if (campaign?.adGroups?.length) {
                    campaign.adGroups[0] = {
                        ...campaign.adGroups[0],
                        adGroupLocations: toCampaignAdGroupLocations(
                            newLocations,
                            campaign.adGroups[0].adGroupLocations ?? []
                        ),
                    };
                } else {
                    campaign.adGroups.push({
                        adGroupLocations: toCampaignAdGroupLocations(newLocations),
                    });
                }

                onChange({
                    adGroups: campaign.adGroups,
                });
            }

            if (onChange) {
                const zipcodes: string[] = [];

                if (newLocations.length) {
                    if ($campaignWizard.$loader) {
                        $campaignWizard.$loader.loading('campaignAttachLocations');
                    }

                    for (const newLocation of newLocations) {
                        const newZipCodes: ZipCode[] = await $locationBoundaries.getLocationBoundaryZipCodes(
                            newLocation,
                            campaign.zipcodeRadius
                        );

                        if (newZipCodes.length) {
                            zipcodes.push(...newZipCodes.map((z: ZipCode) => z.code));
                        }
                    }

                    if ($campaignWizard.$loader) {
                        $campaignWizard.$loader.loaded('campaignAttachLocations');
                    }
                }

                onChange({
                    zipcodes: Array.from(new Set(zipcodes)).join(','),
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onChange, campaign]
    );

    const handleLocationChange = async (newLocations: Location[]): Promise<void> => {
        await updateLocations([...newLocations]);
    };

    const handleRadiusChange = (newRadius: number): void => {
        onChange({
            zipcodeRadius: newRadius,
        });
    };

    useEffect(() => {
        handleRecenterLocation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations]);

    return (
        <Card id="campaign-wizard-targeting-location" variant="outlined">
            <CardHeader
                title={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body1" color="text.primary">
                            Location
                        </Typography>
                    </Stack>
                }
            />
            <CardContent>
                <Row>
                    <FormControl>
                        <Column gap={2}>
                            <FormLabel required={true}>Select Locations to Target Your Audience</FormLabel>

                            <Box>
                                <LocationSourceField
                                    selections={selections}
                                    fetchSelections={fetchSelections}
                                    onChange={handleLocationChange}
                                    items={locations}
                                    radius={campaign.zipcodeRadius}
                                    disableRadius={isCompletedCampaign}
                                    onRadiusChange={handleRadiusChange}
                                    mapCenter={mapCenter}
                                    slotProps={{
                                        button: { disabled: !(isDraftCampaign || isNewCampaign) },
                                    }}
                                />
                            </Box>
                        </Column>

                        {hasError('adGroups.*.adGroupLocations') && (
                            <FormHelperText error={true} sx={{ mt: 2, ml: 0 }}>
                                Location(s) selection is required. Please select at least one location.
                            </FormHelperText>
                        )}
                    </FormControl>
                </Row>
            </CardContent>
        </Card>
    );
}
